import type { LoyaltyCategorySetting } from '../loyaltySettings';

/**
 * The loyalty categories a client can hold, and which one their spending earns.
 *
 * The categories are the rows of Delphi's `bonus` table, as the Configure page's Loyalty tab edits
 * them (`/configuration/loyalty`). The name is what is stored in `client.category` and shown in
 * the Contracts summary, so it is the legacy spelling.
 *
 * A client's category is proposed from their net profit (the interest earned on redeemed
 * contracts, in RON), compared with each category's lower bound (Delphi's `Maimare`). The
 * proposal is only a default: the person at the desk can pick another category before saving it.
 */

/** The categories offered while none can be read, so the dropdown is never empty. */
const DEFAULT_CATEGORY_NAMES = ['Bonus', 'Bonus+', 'Silver', 'Gold', 'VIP'] as const;

export interface LoyaltyCategory {
    /** The value stored on the client. */
    name: string;
    /** Discount granted to the category, in percent. */
    discountPercent: number;
    /** Net profit (RON) from which the category is earned. */
    minThreshold: number;
}

/** The categories, lowest lower bound first. */
export const buildLoyaltyCategories = (
    settings: readonly LoyaltyCategorySetting[] | undefined,
): LoyaltyCategory[] =>
    (settings ?? [])
        .map(({ name, discountPercent, lowerBound }) => ({
            name,
            discountPercent,
            minThreshold: lowerBound,
        }))
        .sort((first, second) => first.minThreshold - second.minThreshold);

/**
 * The highest tier whose lower bound the client's net profit reaches. Below every bound the
 * lowest tier applies, since a client always belongs to one. Null when no tier is configured.
 */
export const proposeLoyaltyCategory = (
    categories: readonly LoyaltyCategory[],
    netProfit: number,
): string | null => {
    let proposed: LoyaltyCategory | undefined = categories[0];
    for (const category of categories) {
        if (netProfit >= category.minThreshold) {
            proposed = category;
        }
    }
    return proposed?.name ?? null;
};

/**
 * The names the category dropdown offers: the configured categories, or the usual five while none
 * could be read. A category the client already holds that is not among them stays selectable, so
 * that opening the dropdown never silently drops it.
 */
export const categoryOptionNames = (
    categories: readonly LoyaltyCategory[],
    currentCategory: string | null | undefined,
): string[] => {
    const names: string[] =
        categories.length > 0 ? categories.map(category => category.name) : [...DEFAULT_CATEGORY_NAMES];
    const current = (currentCategory ?? '').trim();
    return current !== '' && !names.includes(current) ? [...names, current] : names;
};

/** A category picked by hand, with the client it was picked for. */
export interface CategoryOverride {
    clientNr: number;
    name: string;
}

export interface CategoryChoiceInput {
    categories: readonly LoyaltyCategory[];
    /** The selected client's number, or undefined with none selected. */
    clientNr: number | undefined;
    /** The category stored on the selected client. */
    currentCategory: string | null | undefined;
    /** Net profit of the selected client, in RON. */
    netProfit: number;
    /** Whether the selected client's contracts have arrived; the profit means nothing before. */
    contractsLoaded: boolean;
    override: CategoryOverride | null;
}

export interface CategoryChoice {
    /** What the dropdown shows: the manual pick, else the proposal, else nothing. */
    selected: string;
    options: string[];
    /** Whether the shown category is the one the client already holds, so there is nothing to save. */
    unchanged: boolean;
}

/**
 * The dropdown's state. A manual pick wins, but only for the client it was made for, so it
 * lapses by itself when another client is selected. With no client, or before their contracts
 * are in, nothing is proposed.
 */
export const resolveCategoryChoice = (input: CategoryChoiceInput): CategoryChoice => {
    const { categories, clientNr, currentCategory, netProfit, contractsLoaded, override } = input;

    const hasClient = clientNr !== undefined;
    const proposed =
        hasClient && contractsLoaded ? proposeLoyaltyCategory(categories, netProfit) : null;
    const manual = override?.clientNr === clientNr ? override?.name : undefined;
    const selected = manual ?? proposed ?? '';

    return {
        selected,
        options: categoryOptionNames(categories, currentCategory),
        unchanged: selected === (currentCategory ?? '').trim(),
    };
};
